import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import LoginStore from 'stores/LoginStore';
import Constants from 'constants/Constants';
import { useLanguage } from '../../contexts/LanguageContext';
import { useBuilder } from '../../contexts/BuilderContext';
import LanguageToggle from './LanguageToggle';
import AuthButton from './AuthButton';
import TranslationManager from '../Admin/TranslationManager';

const TopBarContainer = styled.nav`
  height: 44px;
  background-color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 24px;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    height: 40px;
    padding: 0 16px;
  }
`;

const TopBarContent = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 20px;
  background-color: rgba(255, 255, 255, 0.25);
`;

// Edit Mode Toggle Button - styled like language toggle
const EditModeToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: ${(props) => (props.$isActive ? 'rgba(40, 167, 69, 0.3)' : 'rgba(255, 255, 255, 0.15)')};
  border: 1px solid ${(props) => (props.$isActive ? 'rgba(40, 167, 69, 0.6)' : 'rgba(255, 255, 255, 0.3)')};
  border-radius: 20px;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${(props) => (props.$isActive ? 'rgba(40, 167, 69, 0.4)' : 'rgba(255, 255, 255, 0.25)')};
    border-color: ${(props) => (props.$isActive ? 'rgba(40, 167, 69, 0.8)' : 'rgba(255, 255, 255, 0.5)')};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.4);
  }

  @media (max-width: 768px) {
    padding: 5px 10px;
    font-size: 11px;
  }
`;

const EditModeIndicator = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => (props.$isActive ? '#28a745' : '#888')};
  transition: background-color 0.3s ease;
`;

// Translation Manager Button
const TranslationButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: rgba(13, 110, 253, 0.3);
  border: 1px solid rgba(13, 110, 253, 0.6);
  border-radius: 20px;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(13, 110, 253, 0.4);
    border-color: rgba(13, 110, 253, 0.8);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.4);
  }

  @media (max-width: 768px) {
    padding: 5px 10px;
    font-size: 11px;
  }
`;

function TopBar() {
  const [authInfo, setAuthInfo] = useState(LoginStore.authInfo);
  const { locale, changeLanguage } = useLanguage();
  const builderContext = useBuilder();
  const { isEditMode = false, toggleEditMode = () => {} } = builderContext || {};
  const [isTranslationModalOpen, setIsTranslationModalOpen] = useState(false);

  useEffect(() => {
    const handleChange = () => setAuthInfo(LoginStore.authInfo);
    LoginStore.addChangeListener(handleChange);
    return () => LoginStore.removeChangeListener(handleChange);
  }, []);

  const isLoggedIn = authInfo?.status === Constants.AUTHED;

  return (
    <>
      <TopBarContainer>
        <TopBarContent>
          {/* Admin controls - only visible when logged in */}
          {isLoggedIn && (
            <>
              <EditModeToggle
                $isActive={isEditMode}
                onClick={toggleEditMode}
                title={isEditMode ? 'Edit Mode: ON' : 'Edit Mode: OFF'}
              >
                <EditModeIndicator $isActive={isEditMode} />
                {isEditMode ? 'Edit ON' : 'Edit OFF'}
              </EditModeToggle>

              <TranslationButton
                onClick={() => setIsTranslationModalOpen(true)}
                title="번역 관리"
              >
                번역
              </TranslationButton>

              <Divider />
            </>
          )}

          <LanguageToggle currentLocale={locale} onToggle={changeLanguage} />
          <Divider />
          <AuthButton isLoggedIn={isLoggedIn} />
        </TopBarContent>
      </TopBarContainer>

      {/* Translation Manager Modal */}
      {isLoggedIn && (
        <TranslationManager
          isOpen={isTranslationModalOpen}
          onClose={() => setIsTranslationModalOpen(false)}
        />
      )}
    </>
  );
}

export default TopBar;
